import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Button,
    Alert,
    Snackbar,
    ToggleButtonGroup,
    ToggleButton,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import IncidentReportsList from "./IncidentReportsList";
import IncidentReportFormDialog from "./IncidentReportFormDialog";
import IncidentReportViewDialog from "./IncidentReportViewDialog";
import DeleteReportDialog from "./DeleteReportDialog";
import ReportService from "../../../services/Report.service";

const CATEGORY_FILTERS = ["All", "Shop", "Field", "Office"];

export default function IncidentReport() {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [category, setCategory] = useState("All");

    const [formDialog, setFormDialog] = useState({ open: false, item: null });
    const [viewDialog, setViewDialog] = useState({ open: false, item: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, report: null });

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const showSnackbar = (message, severity = "success") =>
        setSnackbar({ open: true, message, severity });

    const refresh = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await ReportService.listIncidentReports();
            setReports(Array.isArray(res.data) ? res.data : []);
        } catch {
            setError("Failed to load Incident Reports.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const filteredReports = useMemo(() => {
        if (category === "All") return reports;
        return reports.filter((r) => r.category === category);
    }, [reports, category]);

    const handleSaved = () => {
        refresh();
        showSnackbar(formDialog.item ? "Report updated." : "Report created.");
    };

    const handleDeleted = () => {
        refresh();
        showSnackbar("Report deleted.", "info");
    };

    return (
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1.5,
                    mb: 2,
                }}
            >
                <ToggleButtonGroup
                    value={category}
                    exclusive
                    onChange={(_, val) => val && setCategory(val)}
                    size="small"
                    fullWidth
                    sx={{ flex: 1, display: "flex" }}
                >
                    {CATEGORY_FILTERS.map((opt) => (
                        <ToggleButton key={opt} value={opt} sx={{ flex: 1 }}>
                            {opt}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <Button
                    variant="contained"
                    color="error"
                    startIcon={<AddIcon />}
                    onClick={() => setFormDialog({ open: true, item: null })}
                    sx={{ flexShrink: 0, height: 40 }}
                >
                    New Incident Report
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <IncidentReportsList
                reports={filteredReports}
                loading={loading}
                onView={(report) => setViewDialog({ open: true, item: report })}
                onEdit={(report) => setFormDialog({ open: true, item: report })}
                onDelete={(report) => setDeleteDialog({ open: true, report })}
            />

            <IncidentReportFormDialog
                open={formDialog.open}
                onClose={() => setFormDialog({ open: false, item: null })}
                item={formDialog.item}
                onSaved={handleSaved}
            />

            <IncidentReportViewDialog
                open={viewDialog.open}
                onClose={() => setViewDialog({ open: false, item: null })}
                report={viewDialog.item}
            />

            <DeleteReportDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, report: null })}
                report={deleteDialog.report}
                onDeleted={handleDeleted}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
